import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, CornerDownLeft } from 'lucide-react';

// Global command palette — opens with Cmd/Ctrl+K, jumps to any page.
// `pages` is [{ id, title }]; `onNavigate(id)` performs the routing.
export function CommandPalette({ pages, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? pages.filter(p => p.title.toLowerCase().includes(q)) : pages;
  }, [query, pages]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  if (!open) return null;

  const go = (id) => { setOpen(false); onNavigate(id); };

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[activeIdx]) go(results[activeIdx].id); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(560px, 92vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <Search size={18} color="var(--text-3)" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Jump to a page…"
            aria-label="Search pages"
            style={{ flex: 1, border: 'none', outline: 'none', background: 'none', color: 'var(--text)', fontSize: '15px', fontFamily: 'inherit' }}
          />
          <kbd style={{ fontSize: '11px', color: 'var(--text-3)', border: '1px solid var(--border)', borderRadius: '6px', padding: '2px 6px', fontFamily: 'inherit' }}>Esc</kbd>
        </div>
        <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: '6px' }}>
          {results.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' }}>No matching pages</div>
          ) : results.map((p, i) => (
            <button
              key={p.id}
              onClick={() => go(p.id)}
              onMouseEnter={() => setActiveIdx(i)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '9px', border: 'none', cursor: 'pointer', textAlign: 'left', background: i === activeIdx ? 'var(--bg-secondary)' : 'transparent', color: 'var(--text)', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' }}
            >
              <span style={{ flex: 1 }}>{p.title}</span>
              {i === activeIdx && <CornerDownLeft size={14} color="var(--text-3)" />}
            </button>
          ))}
        </div>
        <div style={{ padding: '9px 16px', borderTop: '1px solid var(--border)', fontSize: '11px', color: 'var(--text-4)', display: 'flex', gap: '14px' }}>
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span style={{ marginLeft: 'auto' }}>⌘/Ctrl + K</span>
        </div>
      </div>
    </div>
  );
}
